// src/pages/Login.js
import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Avatar,
  IconButton,
  InputAdornment,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock, 
  Login as LoginIcon,
  Shield,
  Security,
  Fingerprint
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';

export default function Login() {
  const [view, setView] = useState('login'); // 'login', 'forgotPassword', 'resetSent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(location.state?.message || null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: loginError } = await login({ email, password });
    setLoading(false);

    if (loginError) {
      setError(loginError.message);
    } else {
      const from = location.state?.from?.pathname || "/";
      navigate(from === '/login' ? '/' : from, { replace: true });
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (resetError) {
      setError(resetError.message);
    } else {
      setView('resetSent');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.98
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'forgotPassword':
        return (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box component="form" onSubmit={handleForgotPassword} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Wachtwoord herstellen
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                Geen zorgen! Voer je e-mailadres in en we sturen je een beveiligde link om je wachtwoord opnieuw in te stellen.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                label="E-mail"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
              />
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Herstellink versturen"}
                </Button>
              </motion.div>
              <MuiLink 
                component="button" 
                variant="body2" 
                onClick={() => setView('login')} 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                ← Terug naar inloggen
              </MuiLink>
            </Box>
          </motion.div>
        );
      case 'resetSent':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Shield sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                </motion.div>
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Check je e-mail
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
                We hebben een beveiligde herstellink gestuurd naar<br />
                <strong style={{ color: theme.palette.primary.main }}>{email}</strong>
              </Typography>
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  onClick={() => setView('login')}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Terug naar inloggen
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        );
      default: // 'login'
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="E-mail"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Wachtwoord"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
              />
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? null : <LoginIcon />}
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Veilig Inloggen"}
                </Button>
              </motion.div>
              <MuiLink 
                component="button" 
                variant="body2" 
                onClick={() => setView('forgotPassword')} 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Wachtwoord vergeten?
              </MuiLink>
            </Box>
          </motion.div>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}20 50%, ${theme.palette.primary.main}10 100%)`,
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={cardVariants}>
            <Paper 
              elevation={8} 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                width: '100%', 
                borderRadius: 4,
                background: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.6)' 
                  : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(20px)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 20px 40px -12px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
                      p: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/logo192.png"
                      alt="BEM.report Logo"
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 2,
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </Box>
                </motion.div>
                <Typography variant="h4" component="h1" sx={{ 
                  fontWeight: 'bold',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}>
                  BEM.report
                </Typography>
                {view !== 'resetSent' && (
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mt: 0.5,
                    fontWeight: 500,
                    textAlign: 'center'
                  }}>
                    {view === 'login' ? 'Beveiligde toegang tot jouw projecten' : ''}
                  </Typography>
                )}
              </Box>

              {success && view === 'login' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: 'success.main'
                      }
                    }} 
                    onClose={() => setSuccess(null)}
                  >
                    {success}
                  </Alert>
                </motion.div>
              )}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: 'error.main'
                      }
                    }} 
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}

              {renderContent()}
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}